export type FinanceLedgerIngestionSourceKind =
  | 'connected-bank-feed'
  | 'manual-statement-csv'
  | 'manual-statement-pdf'
  | 'forwarded-email';

export type FinanceLedgerIngestionPath =
  | 'bank-feed'
  | 'statement-import'
  | 'email-forwarded';

export type FinanceLedgerIngestionAutomation = 'automatic' | 'manual' | 'assisted';

export type FinanceStatementImportFormat = 'csv' | 'pdf';

export type FinanceStatementImportAdapterId =
  | 'generic-csv'
  | 'generic-pdf'
  | 'first-direct-pdf';

export type FinanceStatementImportRoutingConfidence = 'high' | 'medium' | 'low';

export interface FinanceStatementImportAdapter {
  id: FinanceStatementImportAdapterId;
  format: FinanceStatementImportFormat;
  label: string;
  institutionLabel: string | null;
  routingConfidence: FinanceStatementImportRoutingConfidence;
  description: string;
  recommendedFallback: string | null;
}

export type FinanceStatementImportReviewState = 'ready' | 'review' | 'fallback-recommended';

export interface FinanceStatementImportReview {
  state: FinanceStatementImportReviewState;
  title: string;
  body: string;
  parsedTransactionCount: number;
  sourceRowCount: number;
  skippedRowCount: number;
  lowConfidenceCount: number;
}

export interface FinanceLedgerIngestionSource {
  kind: FinanceLedgerIngestionSourceKind;
  path: FinanceLedgerIngestionPath;
  label: string;
  shortLabel: string;
  description: string;
  automation: FinanceLedgerIngestionAutomation;
}

type FinanceLedgerBankConnectionSummaryInput = {
  accountCount: number;
  transactionCount: number;
  institutionNames?: Array<string | null | undefined>;
};

type DetectFinanceStatementImportAdapterInput = {
  format: FinanceStatementImportFormat;
  fileName?: string | null | undefined;
  textSample?: string | null | undefined;
  headerKeys?: Array<string | null | undefined>;
};

type BuildFinanceStatementImportReviewInput = {
  adapter: FinanceStatementImportAdapter;
  parsedTransactionCount: number;
  sourceRowCount: number;
  skippedRowCount: number;
  lowConfidenceCount: number;
};

const FINANCE_LEDGER_INGESTION_SOURCE_MAP: Record<
  FinanceLedgerIngestionSourceKind,
  FinanceLedgerIngestionSource
> = {
  'connected-bank-feed': {
    kind: 'connected-bank-feed',
    path: 'bank-feed',
    label: 'Connected bank feed',
    shortLabel: 'Bank feed',
    description: 'Secure bank-feed history imported through the open banking connection.',
    automation: 'automatic',
  },
  'manual-statement-csv': {
    kind: 'manual-statement-csv',
    path: 'statement-import',
    label: 'CSV statement import',
    shortLabel: 'CSV statement',
    description: 'A manually uploaded CSV bank statement saved into the finance ledger.',
    automation: 'manual',
  },
  'manual-statement-pdf': {
    kind: 'manual-statement-pdf',
    path: 'statement-import',
    label: 'PDF statement import',
    shortLabel: 'PDF statement',
    description: 'A manually uploaded text-based PDF bank statement saved into the finance ledger.',
    automation: 'manual',
  },
  'forwarded-email': {
    kind: 'forwarded-email',
    path: 'email-forwarded',
    label: 'Forwarded email import',
    shortLabel: 'Forwarded email',
    description: 'Evidence extracted from a forwarded email and attached to the finance ledger.',
    automation: 'assisted',
  },
};

const FINANCE_STATEMENT_IMPORT_ADAPTERS: Record<
  FinanceStatementImportAdapterId,
  FinanceStatementImportAdapter
> = {
  'generic-csv': {
    id: 'generic-csv',
    format: 'csv',
    label: 'CSV statement layout',
    institutionLabel: null,
    routingConfidence: 'medium',
    description: 'A generic mapped CSV statement layout with header-based column matching.',
    recommendedFallback: null,
  },
  'generic-pdf': {
    id: 'generic-pdf',
    format: 'pdf',
    label: 'Generic PDF statement layout',
    institutionLabel: null,
    routingConfidence: 'low',
    description: 'A generic text-based PDF statement layout using line and balance heuristics.',
    recommendedFallback: 'CSV or a connected bank feed will usually be safer than a generic PDF import.',
  },
  'first-direct-pdf': {
    id: 'first-direct-pdf',
    format: 'pdf',
    label: 'first direct PDF layout',
    institutionLabel: 'first direct',
    routingConfidence: 'high',
    description: 'The running-balance first direct PDF layout with multi-line transaction blocks.',
    recommendedFallback: null,
  },
};

function cloneFinanceLedgerIngestionSource(
  source: FinanceLedgerIngestionSource,
): FinanceLedgerIngestionSource {
  return { ...source };
}

function cloneFinanceStatementImportAdapter(
  adapter: FinanceStatementImportAdapter,
): FinanceStatementImportAdapter {
  return { ...adapter };
}

function formatInstitutionSummary(institutionNames: Array<string | null | undefined>) {
  const uniqueNames = Array.from(
    new Set(
      institutionNames
        .map((entry) => entry?.trim())
        .filter((entry): entry is string => Boolean(entry)),
    ),
  );

  if (uniqueNames.length === 0) {
    return null;
  }

  if (uniqueNames.length === 1) {
    return uniqueNames[0];
  }

  return `${uniqueNames[0]} + ${uniqueNames.length - 1} more`;
}

export function getFinanceLedgerIngestionSource(
  kind: FinanceLedgerIngestionSourceKind,
): FinanceLedgerIngestionSource {
  return cloneFinanceLedgerIngestionSource(FINANCE_LEDGER_INGESTION_SOURCE_MAP[kind]);
}

export function getFinanceStatementImportAdapter(
  id: FinanceStatementImportAdapterId,
): FinanceStatementImportAdapter {
  return cloneFinanceStatementImportAdapter(FINANCE_STATEMENT_IMPORT_ADAPTERS[id]);
}

export function detectFinanceStatementImportAdapter(
  input: DetectFinanceStatementImportAdapterInput,
): FinanceStatementImportAdapter {
  const normalisedFileName = (input.fileName || '').trim().toLowerCase();
  const normalisedTextSample = (input.textSample || '').trim().toLowerCase();
  const headerKeys = (input.headerKeys || [])
    .map((entry) => entry?.trim().toLowerCase())
    .filter((entry): entry is string => Boolean(entry));

  if (input.format === 'pdf') {
    const looksLikeFirstDirect =
      /\bfd statement\b|\bfirst direct\b/.test(normalisedFileName)
      || normalisedTextSample.includes('firstdirect.com')
      || normalisedTextSample.includes('first direct is a division of hsbc uk bank plc')
      || (
        normalisedTextSample.includes('your 1st account details')
        && normalisedTextSample.includes('paid out')
        && normalisedTextSample.includes('paid in')
      );

    return looksLikeFirstDirect
      ? getFinanceStatementImportAdapter('first-direct-pdf')
      : getFinanceStatementImportAdapter('generic-pdf');
  }

  const hasMappableHeaders =
    headerKeys.includes('date')
    || headerKeys.includes('transaction_date')
    || headerKeys.includes('posted_date')
    || headerKeys.includes('booking_date');

  return hasMappableHeaders
    ? getFinanceStatementImportAdapter('generic-csv')
    : getFinanceStatementImportAdapter('generic-csv');
}

export function buildFinanceStatementImportReview(
  input: BuildFinanceStatementImportReviewInput,
): FinanceStatementImportReview {
  const parsedTransactionCount = Math.max(input.parsedTransactionCount, 0);
  const sourceRowCount = Math.max(input.sourceRowCount, parsedTransactionCount);
  const skippedRowCount = Math.max(input.skippedRowCount, sourceRowCount - parsedTransactionCount, 0);
  const lowConfidenceCount = Math.max(input.lowConfidenceCount, 0);
  const lowConfidenceShare = parsedTransactionCount > 0
    ? lowConfidenceCount / parsedTransactionCount
    : 1;

  if (parsedTransactionCount === 0) {
    return {
      state: 'fallback-recommended',
      title: 'Use the safest import path for this statement',
      body: input.adapter.recommendedFallback
        || 'Adulting could not confidently read usable transaction rows from this file yet. Try CSV or a connected bank feed if your bank supports it.',
      parsedTransactionCount,
      sourceRowCount,
      skippedRowCount,
      lowConfidenceCount,
    };
  }

  const shouldEscalateReview =
    input.adapter.routingConfidence === 'low'
    || skippedRowCount >= Math.max(3, Math.round(sourceRowCount * 0.2))
    || (lowConfidenceCount >= 3 && lowConfidenceShare >= 0.35);

  if (shouldEscalateReview) {
    return {
      state: 'review',
      title: 'Check this import before saving',
      body: `${input.adapter.label} was detected, and Adulting kept ${parsedTransactionCount} transactions. ${skippedRowCount > 0 ? `${skippedRowCount} lines still need review or a safer format. ` : ''}Low-confidence matches usually come from vague merchant references, transfers, or layout-specific wording.`,
      parsedTransactionCount,
      sourceRowCount,
      skippedRowCount,
      lowConfidenceCount,
    };
  }

  return {
    state: 'ready',
    title: 'Ready to review',
    body: `${input.adapter.label} was detected and Adulting mapped ${parsedTransactionCount} transactions cleanly enough for a normal review pass before saving.`,
    parsedTransactionCount,
    sourceRowCount,
    skippedRowCount,
    lowConfidenceCount,
  };
}

export function resolveStatementImportLedgerSource(fileName: string | null | undefined) {
  const normalisedFileName = (fileName || '').trim().toLowerCase();
  if (normalisedFileName.endsWith('.pdf')) {
    return getFinanceLedgerIngestionSource('manual-statement-pdf');
  }

  return getFinanceLedgerIngestionSource('manual-statement-csv');
}

export function resolveSavedStatementImportLedgerSource(
  source: Partial<FinanceLedgerIngestionSource> | null | undefined,
  fileName: string | null | undefined,
) {
  if (source?.kind && source.kind in FINANCE_LEDGER_INGESTION_SOURCE_MAP) {
    return getFinanceLedgerIngestionSource(source.kind as FinanceLedgerIngestionSourceKind);
  }

  return resolveStatementImportLedgerSource(fileName);
}

export function buildFinanceLedgerBankConnectionSummary(
  input: FinanceLedgerBankConnectionSummaryInput,
) {
  const source = getFinanceLedgerIngestionSource('connected-bank-feed');
  const institutionSummary = formatInstitutionSummary(input.institutionNames || []);
  const accountLabel = `${input.accountCount} account${input.accountCount === 1 ? '' : 's'}`;
  const institutionPrefix = institutionSummary ? `${institutionSummary} linked. ` : '';

  return {
    title: 'Bank connected',
    body: `${institutionPrefix}${accountLabel} linked and ${input.transactionCount} transactions imported into your money history.`,
    source,
  };
}

export function buildFinanceLedgerStatementImportReviewMessage(
  source: FinanceLedgerIngestionSource,
  transactionCount: number,
  adapter?: FinanceStatementImportAdapter | null,
) {
  const adapterLabel = adapter?.institutionLabel
    ? `${adapter.institutionLabel} ${source.shortLabel.toLowerCase()}`
    : source.shortLabel.toLowerCase();
  return `Imported ${transactionCount} transactions from ${adapterLabel} for review.`;
}
